import { V3Models } from "@istari/istari-client";
import { useCallback } from "react";

import { applyChatSaveFollowUps } from "~/ai-chat/feature/apply-chat-save-follow-ups";
import { useAiChat } from "~/ai-chat/feature/use-ai-chat";
import { buildReferencedFileName } from "~/ai-chat/model/chat-message";
import type { ChatSaveChoices, ChatSaveDestination, ChatSaveOutcome } from "~/ai-chat/model/chat-save";
import { extensionOf, inferMimeType, type SaveResourceAction } from "~/ai-chat/model/save-resource-action";
import { usePatchModelControlTags } from "~/api/useControlTags";
import { useAssignInfosecLevel } from "~/api/useInfosecLevels";
import { useCommitFileToBranch } from "~/branching/feature/use-commit-file-to-branch";
import { useCreateResource } from "~/resource/data-source/use-create-resource";

export interface SavedChatResource extends ChatSaveOutcome {
  resourceId: string;
  fileRevisionId: string;
}

interface SaveResourceActionApi {
  save: (action: SaveResourceAction, choices?: ChatSaveChoices) => Promise<SavedChatResource>;
  isPending: boolean;
}

export function useSaveResourceAction(destination?: ChatSaveDestination): SaveResourceActionApi {
  const { addReferencedFiles, recordSavedArtifact, getConversationEpoch } = useAiChat();
  const createResource = useCreateResource();
  const assignInfosecLevel = useAssignInfosecLevel();
  // The card reports a tag miss in its own warning; the hook's toast would duplicate it and,
  // from a popped-out chat, land in the main window.
  const patchControlTags = usePatchModelControlTags({ onError: () => undefined });
  const commitFileToBranch = useCommitFileToBranch(destination?.systemId);

  const save = useCallback(
    async (action: SaveResourceAction, choices: ChatSaveChoices = {}): Promise<SavedChatResource> => {
      const conversationEpoch = getConversationEpoch();
      const file = new File([action.content], action.fileName, { type: inferMimeType(action.fileName) });
      // Model, not File: the Files explorer lists only Model/Artifact resources
      // (FILES_EXPLORER_RESOURCE_TYPES), and manual uploads default to Model —
      // a "file"-typed resource would be invisible in the library.
      const resource = await createResource.mutateAsync({
        file,
        displayName: action.displayName,
        description: action.description,
        resourceType: V3Models.ResourceTypeDto.Model,
      });

      const outcome = await applyChatSaveFollowUps(
        { resourceId: resource.resourceId, fileRevisionId: resource.fileRevisionId, displayName: action.displayName },
        choices,
        destination,
        {
          assignInfosecLevel: (id, infosecLevelId) =>
            assignInfosecLevel.mutateAsync({ type: "model", id, infosecLevelId }),
          assignControlTags: (modelId, ids) =>
            patchControlTags.mutateAsync({ modelId, controlTagIdsActionTypes: [{ ids, action: "set" }] }),
          addToBranch: (fileRevisionId, displayName, target) =>
            commitFileToBranch.mutateAsync({
              branchId: target.branch.id,
              expectedHeadSnapshotId: target.branch.headCommitId,
              fileRevisionId,
              commitMessage: `Add ${displayName}`,
            }),
        },
      );

      // The save may outlive the chat it started in; a resource saved from the previous
      // conversation must not attach to the composer or the Artifacts panel of the one now on screen.
      if (getConversationEpoch() === conversationEpoch) {
        const name = buildReferencedFileName(action.displayName, extensionOf(action.fileName));
        addReferencedFiles([
          { resourceId: resource.resourceId, name, fileRevisionId: resource.fileRevisionId, origin: "chat-save" },
        ]);
        recordSavedArtifact({ resourceId: resource.resourceId, name });
      }
      return { resourceId: resource.resourceId, fileRevisionId: resource.fileRevisionId, ...outcome };
    },
    [
      createResource,
      assignInfosecLevel,
      patchControlTags,
      commitFileToBranch,
      destination,
      addReferencedFiles,
      recordSavedArtifact,
      getConversationEpoch,
    ],
  );

  return {
    save,
    isPending:
      createResource.isPending ||
      assignInfosecLevel.isPending ||
      patchControlTags.isPending ||
      commitFileToBranch.isPending,
  };
}
